import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCredentialPublication, getCredentialVettingQueue, vetCredentialFlavour } from '../../api';

export default function CredentialsVettingAdmin() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await getCredentialVettingQueue();
      setItems(data.items || []);
    } catch (e) {
      setError(e?.message || 'Could not load the queue.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openItem = async (row) => {
    setSelected(row);
    setNote('');
    setDetail(null);
    try {
      setDetail(await getCredentialPublication(row.propertyId));
    } catch (e) {
      setError(e?.message || 'Could not load this property.');
    }
  };

  const decide = async (decision) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await vetCredentialFlavour(selected.propertyId, { decision, note });
      setSelected(null);
      setDetail(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Could not save the decision.');
    } finally {
      setBusy(false);
    }
  };

  const pending = detail?.pendingFlavour || detail?.flavour;

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold text-slate-800 mb-1">Credential flavour review</h1>
      <p className="text-sm text-slate-500 mb-6">
        Light-touch check of farmer-authored photo, alt text, caption and copy before the public credential page goes live.
      </p>
      {error ? <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div> : null}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h2 className="font-bold text-slate-800 mb-3">Pending ({items.length})</h2>
          {items.length === 0 ? <p className="text-sm text-slate-500">Nothing waiting.</p> : null}
          <ul className="space-y-2">
            {items.map((row) => (
              <li key={row.propertyId}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                  onClick={() => openItem(row)}
                >
                  <div className="font-medium text-slate-800">{row.farmName || row.propertyId}</div>
                  <div className="text-xs text-slate-500">{row.publicAlias || row.propertyId} · {row.submittedAt || ''}</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          {!selected ? <p className="text-sm text-slate-500">Select a submission to review.</p> : null}
          {pending ? (
            <div className="space-y-3 text-sm text-slate-700">
              <div className="font-bold text-slate-800">{pending.managerName || selected.farmName}</div>
              <div className="text-slate-500">{pending.managerRole}</div>
              {pending.photoUrl ? <img src={pending.photoUrl} alt={pending.photoAlt || ''} className="rounded-lg max-h-64" /> : null}
              <p><span className="text-slate-400">Alt:</span> {pending.photoAlt || '—'}</p>
              <p><span className="text-slate-400">Caption:</span> {pending.photoCaption || '—'}</p>
              <p>{pending.farmTagline}</p>
              <p className="whitespace-pre-wrap">{pending.managerBio}</p>
              <p className="whitespace-pre-wrap">{pending.farmDescription}</p>
              {(detail?.warnings || []).map((w) => <p key={w} className="text-amber-700">{w}</p>)}
              <textarea
                className="w-full border border-slate-200 rounded-lg p-2"
                rows={3}
                placeholder="Note to the manager (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="flex gap-2">
                <button type="button" className="px-3 py-2 rounded-lg bg-[#043132] text-white text-sm font-medium disabled:opacity-50" disabled={busy} onClick={() => decide('approve')}>
                  Approve
                </button>
                <button type="button" className="px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium disabled:opacity-50" disabled={busy} onClick={() => decide('reject')}>
                  Request changes
                </button>
                <Link className="px-3 py-2 text-sm text-teal-800 underline" to={`/credentials/${selected.propertyId}`}>
                  Open in app
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
